import { useState, useEffect } from "react";
import { apiFetch } from "../../api/api";
import { T } from "../data/Tokens";
import {
  Card, PageHeader, GoldBtn, GhostBtn, StatusPill, SectionTitle,
  SearchBar, Th, Td, Tr, Tabs, StatCard, EmptyState,
  Field, Input, Select, ConfirmModal
} from "../components/common/Ui";

// Simple coupon = code + flat percentage off + status.
// Type is always "Percentage", shipping is already free site-wide, and
// there's no min order / max discount / usage limit / expiry — kept minimal.
const EMPTY_FORM = { code:"", value:"", status:"Active" };

export default function CouponsAdmin() {
  const [coupons, setCoupons]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [loadError, setLoadError] = useState("");
  const [saving, setSaving]     = useState(false);

  const [tab, setTab]           = useState("all");
  const [search, setSearch]     = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null); // null = add, dbId (number) = edit
  const [form, setForm]         = useState(EMPTY_FORM);
  const [del, setDel]           = useState(null);
  const [toast, setToast]       = useState(null);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const showToast = (msg, color = T.gold) => {
    setToast({ msg, color });
    setTimeout(() => setToast(null), 2500);
  };

  // ── Fetch coupons from backend ──────────────────────────────────────────────
  const fetchCoupons = async () => {
    setLoading(true);
    setLoadError("");
    try {
      const res = await apiFetch("/coupons/list.php");
      setCoupons(res.data || []);
    } catch (err) {
      setLoadError(err.message || "Failed to load coupons.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchCoupons(); }, []);

  // ── Open Add form ──────────────────────────────────────────────────────────
  const openAdd = () => {
    setForm(EMPTY_FORM);
    setEditingId(null);
    setShowForm(true);
  };

  // ── Open Edit form — prefill with coupon data ──────────────────────────────
  const openEdit = (coupon) => {
    setForm({
      code:   coupon.code,
      value:  coupon.value,
      status: coupon.status,
    });
    setEditingId(coupon.dbId);
    setShowForm(true);
  };

  // ── Save (Add or Edit) — backend connected ─────────────────────────────────
  // The backend still supports type/minOrder/maxDiscount/limit/expiry/freeShipping —
  // we just always send sensible defaults so the coupon is a flat "X% off, no strings".
  const handleSave = async () => {
    if (!form.code.trim()) return alert("Coupon code is required!");
    if (!form.value) return alert("Discount % is required!");

    const payload = {
      code:         form.code.toUpperCase(),
      type:         "Percentage",
      value:        form.value,
      minOrder:     "",
      maxDiscount:  "",
      limit:        "",
      expiry:       "",
      freeShipping: false,
      status:       form.status,
    };

    setSaving(true);
    try {
      if (editingId) {
        await apiFetch("/coupons/update.php", {
          method: "PUT",
          body: { dbId: editingId, ...payload },
        });
        showToast("✓ Coupon updated successfully", T.success);
      } else {
        await apiFetch("/coupons/create.php", {
          method: "POST",
          body: payload,
        });
        showToast("✓ Coupon created successfully", T.success);
      }
      await fetchCoupons();
      setShowForm(false);
      setEditingId(null);
      setForm(EMPTY_FORM);
    } catch (err) {
      alert(err.message || "Something went wrong while saving the coupon.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (dbId) => {
    try {
      await apiFetch("/coupons/delete.php", {
        method: "DELETE",
        body: { dbId },
      });
      await fetchCoupons();
      showToast("🗑 Coupon deleted", T.danger);
    } catch (err) {
      alert(err.message || "Failed to delete coupon.");
    } finally {
      setDel(null);
    }
  };

  const handleToggleStatus = async (coupon) => {
    try {
      await apiFetch("/coupons/toggle-status.php", {
        method: "PUT",
        body: { dbId: coupon.dbId },
      });
      await fetchCoupons();
    } catch (err) {
      showToast(err.message || "Failed to toggle status", T.danger);
    }
  };

  // ── Stats (live from real data) ─────────────────────────────────────────────
  const STATS = [
    { icon:"🎟️", label:"Total Coupons", value: coupons.length,                                      sub:"All coupons",       color: T.gold },
    { icon:"✅", label:"Active",         value: coupons.filter(c=>c.status==="Active").length,       sub:"Currently running", color: T.success },
    { icon:"⏱️", label:"Total Uses",     value: coupons.reduce((a,c)=>a+c.uses,0),                  sub:"All time",          color:"#5B7EE5", bg:"#EEF3FF" },
    { icon:"⏸️", label:"Inactive",       value: coupons.filter(c=>c.status==="Inactive").length,     sub:"Turned off",        color: T.textSec, bg:"#F3F3F3" },
  ];

  const TABS = [
    { id:"all",      label:"All",      count: coupons.length },
    { id:"active",   label:"Active",   count: coupons.filter(c=>c.status==="Active").length },
    { id:"inactive", label:"Inactive", count: coupons.filter(c=>c.status==="Inactive").length },
  ];

  const filtered = coupons.filter(c => {
    const ms = !search || c.code.toLowerCase().includes(search.toLowerCase());
    const mt = tab === "all" || c.status.toLowerCase() === tab;
    return ms && mt;
  });

  return (
    <div>
      <PageHeader title="Coupons" subtitle={`${coupons.length} coupons`}>
        <GhostBtn>↓ Export CSV</GhostBtn>
        <GoldBtn onClick={openAdd}>+ Create Coupon</GoldBtn>
      </PageHeader>

      {/* Loading / Error states */}
      {loading && (
        <div className="flex items-center justify-center py-16">
          <div className="flex flex-col items-center gap-2">
            <div className="w-8 h-8 rounded-full animate-spin"
              style={{ border: `3px solid ${T.border}`, borderTopColor: T.gold }} />
            <p className="text-[12px]" style={{ color: T.textSec }}>Loading coupons...</p>
          </div>
        </div>
      )}

      {!loading && loadError && (
        <Card className="p-6 text-center mb-5">
          <p className="text-[13px] font-semibold mb-2" style={{ color: T.danger }}>{loadError}</p>
          <button onClick={fetchCoupons}
            className="px-4 py-2 rounded-xl text-[12px] font-semibold text-white"
            style={{ background: T.gold }}>
            Retry
          </button>
        </Card>
      )}

      {!loading && !loadError && (
        <>
          {/* Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 sm:gap-3 mb-5 sm:mb-6">
            {STATS.map(s => <StatCard key={s.label} {...s} />)}
          </div>

          <Tabs tabs={TABS} active={tab} onChange={setTab} />

          <Card>
            <div className="px-4 sm:px-5 py-3.5 sm:py-4" style={{ borderBottom:`1px solid ${T.border}` }}>
              <SearchBar value={search} onChange={setSearch} placeholder="Search coupon code..." />
            </div>

            {filtered.length === 0
              ? <EmptyState icon="🎟️" title="No coupons found" sub="Create your first coupon." action="Create Coupon" onAction={openAdd} />
              : (
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[560px]">
                    <thead>
                      <tr style={{ borderBottom:`1px solid ${T.border}` }}>
                        <Th>Code</Th><Th>Discount</Th><Th>Uses</Th><Th>Status</Th><Th>Actions</Th>
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.map((c, i) => (
                        <Tr key={c.dbId} last={i === filtered.length - 1}>
                          <Td>
                            <span className="text-[13px] font-bold font-mono px-2.5 py-1 rounded-lg"
                              style={{ background:T.goldL, color:T.goldD }}>{c.code}</span>
                          </Td>
                          <Td>
                            <span className="text-[13px] font-bold" style={{ color:T.textPri }}>
                              {c.value}% off
                            </span>
                          </Td>
                          <Td>
                            <span className="text-[13px] font-bold" style={{ color:T.textPri }}>{c.uses}</span>
                            <span className="text-[11px] ml-1" style={{ color:T.textSec }}>times used</span>
                          </Td>
                          <Td>
                            <button onClick={() => handleToggleStatus(c)}
                              className="cursor-pointer transition-all hover:opacity-80">
                              <StatusPill s={c.status} />
                            </button>
                          </Td>
                          <Td>
                            <div className="flex gap-1">
                              <button
                                onClick={() => openEdit(c)}
                                className="px-2.5 py-1.5 rounded-lg text-[11px] font-medium border transition-all hover:bg-amber-50 active:scale-95"
                                style={{ borderColor:T.gold, color:T.gold }}>
                                ✏️ Edit
                              </button>
                              <button
                                onClick={() => setDel(c.dbId)}
                                className="px-2.5 py-1.5 rounded-lg text-[11px] font-medium border transition-all hover:bg-red-50 active:scale-95"
                                style={{ borderColor:T.border, color:T.danger }}>
                                🗑
                              </button>
                            </div>
                          </Td>
                        </Tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
          </Card>
        </>
      )}

      {/* ── Add / Edit Coupon Modal ── */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-0 sm:px-4" style={{ background:"rgba(0,0,0,0.5)" }}>
          <div className="w-full h-full sm:h-auto sm:max-w-md rounded-none sm:rounded-3xl overflow-hidden sm:max-h-[90vh] flex flex-col"
            style={{ background:T.card, boxShadow:"0 20px 60px rgba(0,0,0,0.2)" }}>

            {/* Header */}
            <div className="flex items-center justify-between px-4 sm:px-6 py-3.5 sm:py-4 flex-shrink-0"
              style={{ borderBottom:`1px solid ${T.border}` }}>
              <div className="min-w-0">
                <h2 className="text-[16px] sm:text-[17px] font-bold" style={{ color:T.textPri, fontFamily:T.serif }}>
                  {editingId ? "Edit Coupon" : "Create Coupon"}
                </h2>
                <p className="text-[11px] truncate" style={{ color:T.textSec }}>
                  {editingId ? `Editing: ${form.code}` : "Fill in the coupon details below"}
                </p>
              </div>
              <button onClick={() => { if (!saving) { setShowForm(false); setEditingId(null); setForm(EMPTY_FORM); } }}
                className="text-gray-400 hover:text-gray-700 text-2xl leading-none flex-shrink-0 ml-2">×</button>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-4">
              <Field label="Coupon Code" required>
                <Input
                  value={form.code}
                  onChange={e => set("code", e.target.value.toUpperCase())}
                  placeholder="WELCOME20" />
              </Field>

              <Field label="Discount %" required>
                <Input
                  value={form.value}
                  onChange={e => set("value", e.target.value)}
                  placeholder="10"
                  type="number" />
              </Field>

              <Field label="Status">
                <Select value={form.status} onChange={e => set("status", e.target.value)}>
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                </Select>
              </Field>

              {/* Preview */}
              {form.code && (
                <div className="rounded-2xl p-4" style={{ background:T.goldL, border:`1px solid ${T.champagne}` }}>
                  <p className="text-[10px] font-semibold uppercase tracking-wider mb-2" style={{ color:T.goldD }}>Preview</p>
                  <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
                    <span className="text-[14px] sm:text-[15px] font-bold font-mono" style={{ color:T.goldD }}>{form.code||"CODE"}</span>
                    <span className="text-[12px] sm:text-[13px] font-semibold" style={{ color:T.textPri }}>
                      {form.value||"?"}% off entire order
                    </span>
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between gap-2 px-4 sm:px-6 py-3.5 sm:py-4 flex-shrink-0"
              style={{ borderTop:`1px solid ${T.border}` }}>
              <button onClick={() => { if (!saving) { setShowForm(false); setEditingId(null); setForm(EMPTY_FORM); } }} disabled={saving}
                className="px-4 py-2 rounded-xl text-[13px] font-medium border disabled:opacity-50"
                style={{ borderColor:T.border, color:T.textSec }}>Cancel</button>
              <button onClick={handleSave} disabled={saving}
                className="px-5 py-2.5 rounded-xl text-[13px] font-semibold text-white transition-all hover:shadow-md active:scale-95 disabled:opacity-60"
                style={{ background:T.gold }}>
                {saving ? "Saving..." : (editingId ? "✓ Update Coupon" : "✓ Create Coupon")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirm */}
      {del && (
        <ConfirmModal
          title="Delete Coupon?"
          message={`Coupon "${coupons.find(c=>c.dbId===del)?.code}" will be permanently deleted.`}
          onConfirm={() => handleDelete(del)}
          onCancel={() => setDel(null)}
        />
      )}

      {/* Toast */}
      {toast && (
        <div className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-6 sm:bottom-6 z-[100] px-5 py-3 rounded-2xl text-white text-[13px] font-semibold shadow-2xl text-center sm:text-left"
          style={{ background:toast.color }}>
          {toast.msg}
        </div>
      )}
    </div>
  );
}